/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";

import qs from "query-string";
import { Trans } from "react-i18next";
import { useWindowSize } from "react-use";
import { Navigate, useNavigate, useLocation } from "react-router-dom";

import { useMutation } from "@tanstack/react-query";

import {
  GridCell,
  GridVideoView,
  useRoomConnection,
  VideoGrid,
  // eslint-disable-next-line import/no-unresolved
} from "@whereby.com/browser-sdk/react";

import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import Title from "../../components/Title";
import Curve from "../../components/Animations/PageTransitions/Curve";
import BackButton from "../../components/BackButton";

import Microphone from "../../assets/icons/Microphone.svg";
import MicrophoneOff from "../../assets/icons/MicrophoneOff.svg";

import { MUTATION_DELETE_VIDEO_ROOM } from "../../config/graphql/mutation";
import { client } from "../../bit/board/hooks/useGraphQLClient";

const MuiGridCell = styled(GridCell)(({ theme }) => ({
  borderRadius: "1rem",
  border: `0.5rem solid ${theme.palette.primary.main}`,
  background: "black",
}));

const Controls = styled("div")({
  display: "flex",
  transform: "translateY(-100%)",
});

const ControlsButton = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "2rem",
  margin: "1rem",
  border: "2px solid white",
  borderRadius: "1rem",
  background: "rgba(35, 35, 35, 0.725)",

  h1: {
    color: "white",
    marginRight: "1rem",
  },
}));

const Room = React.memo(({ roomUrl }: { roomUrl: string }) => {
  const navigate = useNavigate();

  const { width, height } = useWindowSize();

  const [isMicActive, setIsMicActive] = useState<boolean>(true);

  const orientation = width < height ? "portrait" : "landscape";

  const { state, actions } = useRoomConnection(roomUrl, {
    localMediaOptions: {
      audio: true,
      video: true,
    },
  });

  const { remoteParticipants, connectionStatus } = state;

  const { joinRoom, leaveRoom, toggleCamera, toggleMicrophone } = actions;

  useEffect(() => {
    joinRoom();

    return () => {
      leaveRoom();
    };
  }, [joinRoom, leaveRoom]);

  useEffect(() => {
    if (connectionStatus === "left") {
      navigate("/");
    }
  }, [connectionStatus]);

  return (
    <div
      className={`${
        remoteParticipants.length === 0 && orientation === "landscape"
          ? "w-25"
          : "w-50"
      } ${
        remoteParticipants.length === 0 && orientation !== "landscape"
          ? "h-50"
          : "h-100"
      } p-5`}
    >
      <VideoGrid
        videoGridGap={10}
        renderParticipant={({ participant }) => {
          return (
            <MuiGridCell participant={participant} className="video-grid-cell">
              <GridVideoView muted={participant.isLocalClient} />
              {connectionStatus === "connected" &&
                participant.isLocalClient && (
                  <Controls className="d-flex justify-content-between p-3">
                    <ControlsButton
                      onClick={() => {
                        leaveRoom();
                      }}
                    >
                      <Typography variant="h3" className="text-white">
                        <Trans i18nKey="videoCall:endCall" />
                      </Typography>
                      {/* <EndCall width="3.5rem" fill="white" /> */}
                    </ControlsButton>
                    <ControlsButton
                      onClick={() => {
                        setIsMicActive((prev) => !prev);
                        toggleMicrophone();
                      }}
                    >
                      {isMicActive ? (
                        <Microphone width="3.5rem" fill="white" />
                      ) : (
                        <MicrophoneOff width="3.5rem" fill="white" />
                      )}
                    </ControlsButton>
                  </Controls>
                )}
            </MuiGridCell>
          );
        }}
      />
    </div>
  );
});

const VideoCall = () => {
  const { search } = useLocation();

  const query = qs.parse(search);

  const roomId = query.roomId as string;
  const roomUrl = query.roomUrl as string;

  const { mutate: onLeaveRoom } = useMutation(async (id: string) => {
    return client.request(MUTATION_DELETE_VIDEO_ROOM, { id });
  });

  useEffect(() => {
    return () => {
      if (roomId) {
        onLeaveRoom(roomId);
      }
    };
  }, []);

  if (!(roomUrl && roomId)) {
    return Navigate({ to: "/" });
  }

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column h-100">
        <BackButton to="/" />
        <Title>
          <Typography variant="h1">
            <Trans i18nKey="videoCall:title" />
          </Typography>
        </Title>
        <Room roomUrl={roomUrl} />
      </div>
    </Curve>
  );
};

export default VideoCall;
